import { type IMediator } from '#/core/mediator';

import { type ICookieService } from '#/services/cookie';
import { CookieName } from '#/services/cookie/cookie.types';
import { type IDeviceService } from '#/services/device';
import { DeviceType, type FeaturesOptions } from '#/services/device/device.types';
import { type IUrlParamsService, UrlParamsName } from '#/services/url-params';

import { safeJsonParse } from '#/shared/utils/safe-json-parse';

import { type AdditionalSymbolInfo, SYMBOL_INFO_URL_PARAMS } from '#/types/additional-symbol-info.types';

import { SERVICES_TYPES } from '#/services-types';

import { type IConfigProviderService } from './config-provider.types';
import { safeNumberParse } from './safe-number-parse.util';

export class ConfigProviderService implements IConfigProviderService {
  get #cookieService() {
    return this._mediator.get<ICookieService>(SERVICES_TYPES.CookieService);
  }

  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }

  get #deviceService() {
    return this._mediator.get<IDeviceService>(SERVICES_TYPES.DeviceService);
  }

  get #isNativeApp(): boolean {
    const deviceType = this.#deviceService.deviceType;

    return deviceType === DeviceType.AndroidNative || deviceType === DeviceType.IosNative;
  }

  constructor(private readonly _mediator: IMediator) {}

  get featureOptions(): FeaturesOptions | undefined {
    return this.#isNativeApp ? this.#getFeatureOptionsFromCookie() : this.#getFeatureOptionsFromUrl();
  }

  get brandName(): string | undefined {
    return this.#urlParamsService.getParamByName(UrlParamsName.BrandName) || undefined;
  }

  get symbolInfo(): AdditionalSymbolInfo | undefined {
    return this.#isNativeApp ? this.#getSymbolInfoFromCookie() : this.#getSymbolInfoFromUrl();
  }

  #getFeatureOptionsFromCookie(): FeaturesOptions | undefined {
    return safeJsonParse(this.#cookieService.get(CookieName.FeaturesOptions));
  }

  #getSymbolInfoFromCookie(): AdditionalSymbolInfo | undefined {
    return safeJsonParse<AdditionalSymbolInfo>(this.#cookieService.get(CookieName.AdditionalSymbolInfo));
  }

  #getFeatureOptionsFromUrl(): FeaturesOptions | undefined {
    const chartSize = this.#urlParamsService.getParamByName(UrlParamsName.ChartSize);
    const openedPositions = this.#urlParamsService.getParamByName(UrlParamsName.FeatureOpenedPositions);
    const pendingOrders = this.#urlParamsService.getParamByName(UrlParamsName.FeaturePendingOrders);
    const tradingOperationsDrag = this.#urlParamsService.getParamByName(UrlParamsName.FeatureTradingOperationsDrag);
    const hmr = this.#urlParamsService.getParamByName(UrlParamsName.FeatureHmr);
    const hmrIcon = this.#urlParamsService.getParamByName(UrlParamsName.FeatureHmrIcon);
    const dealsHistory = this.#urlParamsService.getParamByName(UrlParamsName.FeatureDealsHistory);
    const pnl = this.#urlParamsService.getParamByName(UrlParamsName.FeaturePnl);
    const slTp = this.#urlParamsService.getParamByName(UrlParamsName.FeatureSlTp);
    const chartStateStorage = this.#urlParamsService.getParamByName(UrlParamsName.FeatureChartStateStorage);
    const defaultMovingAverages = this.#urlParamsService.getParamByName(UrlParamsName.FeatureDefaultMovingAverages);
    const chartTemplates = this.#urlParamsService.getParamByName(UrlParamsName.FeatureChartTemplates);

    const tradeConfiguration: FeaturesOptions['trade-configuration'] = {};

    if (openedPositions !== null) {
      tradeConfiguration['opened-positions'] = true;
    }

    if (pendingOrders !== null) {
      tradeConfiguration['pending-orders'] = true;
    }

    if (tradingOperationsDrag !== null) {
      tradeConfiguration['trading-operations-drag'] = true;
    }

    if (hmr !== null) {
      tradeConfiguration.hmr = true;
    }

    if (dealsHistory !== null) {
      tradeConfiguration['deals-history'] = true;
    }

    if (pnl !== null) {
      tradeConfiguration.pnl = true;
    }

    if (slTp !== null) {
      tradeConfiguration['sl-tp'] = true;
    }

    const result: FeaturesOptions = {};

    if (chartSize !== null) {
      result['chart-size'] = true;
    }

    if (hmrIcon !== null) {
      result['hmr-icon'] = true;
    }

    if (chartStateStorage !== null) {
      result['chart-state-storage'] = true;
    }

    if (defaultMovingAverages !== null) {
      result['default-moving-averages'] = true;
    }

    if (chartTemplates !== null) {
      result['chart-templates'] = true;
    }

    if (Object.keys(tradeConfiguration).length > 0) {
      result['trade-configuration'] = tradeConfiguration;
    }

    return result;
  }

  #getSymbolInfoFromUrl(): AdditionalSymbolInfo | undefined {
    const symbol = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.symbol);

    if (!symbol) {
      return undefined;
    }

    const symbolGroup = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.symbolGroup);
    const description = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.description);
    const digits = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.digits);
    const currencySource = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.currencySource);
    const volumePrecision = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.volumePrecision);
    const popularity = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.popularity);
    const volumeMin = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.volumeMin);
    const volumeStep = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.volumeStep);
    const symbolView = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.symbolView);
    const currencyBase = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.currencyBase);
    const currencyProfit = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.currencyProfit);
    const volumeMax = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.volumeMax);
    const contractSize = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.contractSize);
    const tradeMode = this.#urlParamsService.getParamByName(SYMBOL_INFO_URL_PARAMS.tradeMode);

    return {
      symbol: symbol.toUpperCase(),
      symbolGroup: symbolGroup ?? '',
      description: description ?? '',
      digits: safeNumberParse(digits),
      currencySource: currencySource ?? '',
      volumePrecision: safeNumberParse(volumePrecision),
      popularity: safeNumberParse(popularity),
      volumeMin: safeNumberParse(volumeMin),
      volumeStep: safeNumberParse(volumeStep),
      symbolView: symbolView ?? '',
      currencyBase: currencyBase ?? '',
      currencyProfit: currencyProfit ?? '',
      volumeMax: safeNumberParse(volumeMax),
      contractSize: safeNumberParse(contractSize),
      tradeMode: safeNumberParse(tradeMode),
    };
  }
}
